import React, { useState } from 'react';
import {
  Button,
  Popover,
  MenuList,
  MenuItem,
  Tooltip,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Globe, ChevronDown } from 'lucide-mui';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import supportedLanguages, { LanguageCodeType } from './supportedLanguages';
import { updateSettings } from '../../../settings/settingsSlice';

interface LanguageSelectProps {
  noCollapse?: boolean;
}

const LanguageSelect: React.FC<LanguageSelectProps> = ({ noCollapse }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallOrMedium = useMediaQuery(theme.breakpoints.down('md'));
  const recorder = useSelector((state: RootState) => state.settings.recorder);
  const defaultTranscriptionLanguage =
    recorder.otherSettings?.defaultTranscriptionLanguage || undefined;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'language-select-popover' : undefined;

  const handleUpdateLanguageSettings = (language: LanguageCodeType) => {
    dispatch(
      updateSettings({
        key: 'recorder',
        newSettings: {
          ...recorder,
          otherSettings: {
            ...recorder.otherSettings,
            defaultTranscriptionLanguage: language,
          },
        },
      })
    );
    handleClose();
  };

  const topLanguages = supportedLanguages.slice(0, 8);
  const otherLanguages = supportedLanguages.slice(8);

  return (
    <>
      <Tooltip title="Select your transcription language" arrow>
        {isSmallOrMedium && !noCollapse ? (
          <IconButton color="secondary" onClick={handleClick}>
            <Globe />
          </IconButton>
        ) : (
          <Button
            color="secondary"
            variant="outlined"
            onClick={handleClick}
            endIcon={<ChevronDown />}
          >
            {
              supportedLanguages.find(
                (lang) => lang.value === defaultTranscriptionLanguage
              )?.label
            }
          </Button>
        )}
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuList>
          {topLanguages.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === defaultTranscriptionLanguage}
              onClick={() => handleUpdateLanguageSettings(option.value)}
            >
              {option.label}
            </MenuItem>
          ))}
          <Divider />
          {otherLanguages.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === defaultTranscriptionLanguage}
              onClick={() => handleUpdateLanguageSettings(option.value)}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};

export default LanguageSelect;
